import { useCallback } from 'react'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_SMS_ANALYTICS_HERO_STATS } from 'src/graphql/smsAnalytics.graphql'
import {
  HeroStatCard,
  type HeroStatDelta,
} from 'src/pages/AnalyticsPage/components/HeroStatCard'
import {
  buildDelta,
  formatCount,
  percentOf,
  toDateRangeBounds,
} from 'src/pages/AnalyticsPage/components/heroStatHelpers'
import { useRefetchOnKeyChange } from 'src/pages/AnalyticsPage/components/trendChartHelpers'

import type { DateRangeFilter, SMSLogFiltersInput } from 'types/graphql'

/** Formats seconds as hours and minutes only (no seconds). */
const formatTimeSaved = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—'
  const s = Math.max(0, Math.round(seconds))
  if (s === 0) return '0m'
  const hours = Math.floor(s / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  return `${minutes}m`
}

const buildTimeSavedDelta = (
  current: number | null | undefined,
  previous: number | null | undefined,
  currentDateRange?: DateRangeFilter | null
): HeroStatDelta | undefined => {
  if (current == null || previous == null) return undefined

  const rawChange = current - previous
  const isFlat = Math.abs(rawChange) < 0.05
  const valueWentUp = rawChange > 0
  const direction: HeroStatDelta['direction'] = isFlat
    ? 'neutral'
    : valueWentUp
    ? 'up'
    : 'down'

  const label = formatTimeSaved(Math.abs(rawChange))
  const tooltipDelta = `${rawChange >= 0 ? '+' : '−'}${formatTimeSaved(
    Math.abs(rawChange)
  )}`

  let tooltip: string
  const dateRangeBounds = toDateRangeBounds(currentDateRange)
  if (dateRangeBounds) {
    const dayMs = 24 * 60 * 60 * 1000
    const numDays = Math.round(
      (dateRangeBounds.lte.getTime() - dateRangeBounds.gte.getTime()) / dayMs
    )
    tooltip =
      `This period (${numDays} days): ${formatTimeSaved(current)}\n` +
      `Prior period (${numDays} days): ${formatTimeSaved(previous)}\n` +
      `Δ ${tooltipDelta}`
  } else {
    tooltip =
      `This period: ${formatTimeSaved(current)}\n` +
      `Prior period: ${formatTimeSaved(previous)}\n` +
      `Δ ${tooltipDelta}`
  }

  return {
    direction,
    label,
    tooltip,
  }
}

const SMSAnalyticsHeroStrip = ({
  filters,
  refreshKey,
}: {
  filters: SMSLogFiltersInput
  refreshKey: number
}) => {
  const onError = useCallback(() => {
    toast('Failed to load SMS analytics summary', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_SMS_ANALYTICS_HERO_STATS, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  useRefetchOnKeyChange(refetch, refreshKey)

  const stats = data?.smsAnalyticsHeroStats
  const current = stats?.current
  const previous = stats?.previous

  const totalDelta = buildDelta({
    current: current?.totalSms,
    previous: previous?.totalSms,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const outboundDelta = buildDelta({
    current: current?.outboundSms,
    previous: previous?.outboundSms,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const inboundDelta = buildDelta({
    current: current?.inboundSms,
    previous: previous?.inboundSms,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const automatedDelta = buildDelta({
    current: current?.automatedSms,
    previous: previous?.automatedSms,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const timeSavedDelta = buildTimeSavedDelta(
    current?.timeSavedSeconds,
    previous?.timeSavedSeconds,
    filters.dateRange
  )

  const outboundShare = percentOf(current?.outboundSms, current?.totalSms)
  const inboundShare = percentOf(current?.inboundSms, current?.totalSms)
  const automatedShare = percentOf(current?.automatedSms, current?.outboundSms)

  return (
    <div
      className="mb-4 grid grid-cols-5 gap-3"
      data-testid="sms-analytics-hero-strip"
    >
      <HeroStatCard
        label="Total SMS"
        value={formatCount(current?.totalSms)}
        loading={loading}
        loadingShowDelta
        delta={totalDelta}
        info={
          'All SMS in the period (inbound + outbound, automated + manual).' +
          (current
            ? `\n\n${formatCount(current.inboundSms)} inbound · ${formatCount(
                current.outboundSms
              )} outbound`
            : '')
        }
      />
      <HeroStatCard
        label="Outbound SMS"
        value={formatCount(current?.outboundSms)}
        loading={loading}
        loadingShowDelta
        delta={outboundDelta}
        info={
          'SMS we sent to candidates.' +
          (outboundShare ? `\n\n${outboundShare} of total SMS` : '')
        }
      />
      <HeroStatCard
        label="Inbound SMS"
        value={formatCount(current?.inboundSms)}
        loading={loading}
        loadingShowDelta
        delta={inboundDelta}
        info={
          'SMS candidates sent to us.' +
          (inboundShare ? `\n\n${inboundShare} of total SMS` : '')
        }
      />
      <HeroStatCard
        label="Automated SMS"
        value={formatCount(current?.automatedSms)}
        loading={loading}
        loadingShowDelta
        delta={automatedDelta}
        info={
          'SMS sent via automated rules, bulk actions, or other system actions.' +
          (automatedShare ? `\n\n${automatedShare} of outbound SMS` : '')
        }
      />
      <HeroStatCard
        label="Time Saved"
        value={formatTimeSaved(current?.timeSavedSeconds)}
        loading={loading}
        loadingShowDelta
        delta={timeSavedDelta}
        featured
        info="Total time saved from SMS — automated rules, bulk actions, and other system actions (15 seconds per message)."
      />
    </div>
  )
}

export default SMSAnalyticsHeroStrip
